import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import { DelegationOverride } from '@app/core/models/datawell/delegations.model';
import { InvoiceDelegationDetail } from '@app/core/models/invoices/invoice-delegation.model';
import { InvoiceDetailModel } from '@app/core/models/invoices/invoice-detail.model';
import { InvoiceParameters } from '@app/core/models/invoices/invoice-parameters.model';
import { SupplierModel } from '@app/core/models/supplier/supplier.model';
import { BuildFormGroupsService } from '@app/core/services/manageInvoice/build-form-groups.service';
import { CommonLibraryService } from '@app/core/services/manageInvoice/common-library.service';
import { DelegationLibraryService } from '@app/core/services/manageInvoice/delegation-library.service';
import * as _ from 'lodash';
import { IDropdownSettings } from 'ng-multiselect-dropdown';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-invoice-delegation',
  templateUrl: './invoice-delegation.component.html',
  styleUrls: ['./invoice-delegation.component.scss'],
})
export class InvoiceDelegationComponent implements OnInit, AfterViewInit {
  unsubscribe$: Subject<boolean> = new Subject<boolean>();

  @Input() invoiceDetailModel: InvoiceDetailModel;
  @Input() invoiceDetailForm: UntypedFormGroup;

  @Input() dropDownSettingsDelegates: IDropdownSettings;
  @Input() dropDownSettingsCreditors: IDropdownSettings;

  @Input() delegates: SupplierModel[];
  @Input() creditors: SupplierModel[];

  @Input() selectedDelegations: DelegationOverride[];

  @Input() invoiceParams: InvoiceParameters;
  @Input() isReadOnlyForm: boolean;
  @Input() isReadOnlyFormOrWaiting: boolean;

  @Input() editedDropdowns: string[];

  @Output() filterChangeDelegates: EventEmitter<any> = new EventEmitter<any>();
  @Output() filterChangeCreditors: EventEmitter<any> = new EventEmitter<any>();

  @Output() onDelegationsChange: EventEmitter<UntypedFormArray> =
    new EventEmitter<UntypedFormArray>();
  @Output() onDelegationsPatched: EventEmitter<UntypedFormArray> =
    new EventEmitter<UntypedFormArray>();

  subcriptValueChanges: boolean = false;

  constructor(
    public commonLibrary: CommonLibraryService,
    public formInvoiceBuilder: BuildFormGroupsService,
    public delegationLibrary: DelegationLibraryService,
    private formBuilder: UntypedFormBuilder
  ) {}

  ngOnInit(): void {
    this.setValueChangeSubscription();
  }

  setValueChangeSubscription() {
    this.subcriptValueChanges =
      this.InvoiceDelegations != undefined &&
      this.InvoiceDelegations.length > 0;

    if (this.subcriptValueChanges) {
      this.InvoiceDelegations.valueChanges
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(() => {
          this.onDelegationsChange.emit(this.InvoiceDelegations);
        });
    }
  }

  ngAfterViewInit(): void {
    this.patchDelegations();
  }

  get InvoiceDelegations() {
    return this.invoiceDetailForm.controls[
      'InvoiceDelegations'
    ] as UntypedFormArray;
  }

  patchDelegations() {
    if (this.invoiceDetailModel.Delegations) {
      this.invoiceDetailModel.Delegations.sort((a) => a.CreatedAt).forEach(
        (x) => {
          this.InvoiceDelegations.push(this.addDelegationFormGroup(x));
        }
      );
      this.onDelegationsPatched.emit(this.InvoiceDelegations);
    }
  }

  addDelegation() {
    const delegation: InvoiceDelegationDetail = new InvoiceDelegationDetail();
    delegation.CreatedAt = Date.now();
    this.invoiceDetailModel.Delegations.push(delegation);

    this.InvoiceDelegations.push(this.addDelegationFormGroup(delegation));

    this.formInvoiceBuilder.markFormGroupTouched(this.invoiceDetailForm);
    this.setValueChangeSubscription();
  }

  addDelegationFormGroup(
    delegation: InvoiceDelegationDetail
  ): UntypedFormGroup {
    return this.formInvoiceBuilder.buildDelegationFormGroup(
      delegation,
      this.invoiceParams,
      this.isReadOnlyForm
    );
  }

  deleteDelegation(index: number, CreatedAt: number) {
    const searchIndex: number = this.invoiceDetailModel.Delegations.findIndex(
      (x) => x.CreatedAt == CreatedAt
    );
    this.invoiceDetailModel.Delegations.splice(searchIndex, 1);
    this.InvoiceDelegations.removeAt(index);
    this.setValueChangeSubscription();
  }

  deleteAllDelegation() {
    this.invoiceDetailModel.Delegations = [];
    this.invoiceDetailForm.controls['InvoiceDelegations'] =
      this.formBuilder.array([]);
    this.setValueChangeSubscription();
  }

  isEmptyDelegateName(index: number): boolean {
    const item = this.InvoiceDelegations.at(index) as UntypedFormGroup;
    const control = item.controls.DelegateName;
    if (!control.value) return true;
    return this.commonLibrary.isEmptyTextValue(control.value[0]);
  }

  isEmptyCreditorName(index: number): boolean {
    const item = this.InvoiceDelegations.at(index) as UntypedFormGroup;
    const control = item.controls.CreditorName;
    if (!control.value) return true;
    return this.commonLibrary.isEmptyTextValue(control.value[0]);
  }

  onChangeDelegateName(
    index: number,
    CreatedAt: number,
    selectedSupplier?: {
      extMdmCode: number;
      CompanyNameAndSiret: string;
    } | null
  ): void {
    this.editedDropdowns.push('DelegateName');
    const supplierDatas = _.find(
      this.delegates,
      (x) => x?.extMdmCode === selectedSupplier?.extMdmCode
    );

    const searchselectedDelegations = _.find(
      this.selectedDelegations,
      (x) => x.evaluaId === supplierDatas?.extMdmCode
    );

    var element = this.invoiceDetailModel.Delegations.find(
      (c) => c.CreatedAt == CreatedAt
    );
    element.DelegateId = supplierDatas?.extMdmCode;
    element.DelegateName = supplierDatas?.CompanyName;
    element.AmountPackageTaxIncluded = searchselectedDelegations?.forfaitTTC;

    var item = this.InvoiceDelegations.at(index) as UntypedFormGroup;
    item.controls.DelegateId.patchValue(supplierDatas?.extMdmCode);
    item.controls.DelegateName.patchValue([supplierDatas?.CompanyName]);
    item.controls.DelegateAmountPackageTaxIncluded.patchValue(
      searchselectedDelegations?.forfaitTTC
    );
  }

  onChangeCreditorName(
    index: number,
    CreatedAt: number,
    selectedSupplier?: {
      extMdmCode: number;
      CompanyNameAndSiret: string;
    } | null
  ): void {
    this.editedDropdowns.push('CreditorName');
    const supplierDatas = _.find(
      this.creditors,
      (x) => x?.extMdmCode === selectedSupplier?.extMdmCode
    );

    var element = this.invoiceDetailModel.Delegations.find(
      (c) => c.CreatedAt == CreatedAt
    );
    element.CreditorId = supplierDatas?.extMdmCode;
    element.CreditorName = supplierDatas?.CompanyName;

    var item = this.InvoiceDelegations.at(index) as UntypedFormGroup;
    item.controls.CreditorId.patchValue(supplierDatas?.extMdmCode);
    item.controls.CreditorName.patchValue([supplierDatas?.CompanyName]);
  }

  onFilterChangeDelegates(data: any) {
    this.filterChangeDelegates.emit(data);
  }

  onFilterChangeCreditors(data: any) {
    this.filterChangeCreditors.emit(data);
  }
}
